/**
 * ClientPrice — a catalogue price as the client pays it (QA-648).
 *
 * With a workspace discount the discounted price is shown with the list price struck beside it; the cart
 * keeps quoting list prices and its own discount line. A price the channel already strikes (`base`) stays
 * the struck figure, and a free price strikes nothing more.
 */

import React from 'react'

import { formatPrice } from 'utils/price/priceFormatter'
import { discountedPrice } from '../store/workspaceDiscount'
import { useWorkspaceDiscount } from './workspaceDiscountContext'
import { StruckPrice } from './StruckPrice.component'

interface Props {
  unitPrice: string
  currency: string
  /** What the channel would otherwise charge, already struck by the backend. */
  baseUnitPrice?: string | null
  /** Same options as `formatPrice`. */
  formatOptions?: Parameters<typeof formatPrice>[3]
  variant?: 'text-sm' | 'text-md'
  free?: boolean
}

export const ClientPrice: React.FC<Props> = ({ unitPrice, currency, baseUnitPrice, formatOptions, variant, free }) => {
  const discount = useWorkspaceDiscount()
  const format = (amount: string | number) => formatPrice(amount, currency, undefined, formatOptions)
  const discounted = Number(unitPrice) > 0 ? discountedPrice(unitPrice, discount) : null
  const base = baseUnitPrice ?? (discounted ? unitPrice : null)

  return (
    <StruckPrice
      price={format(discounted ? discounted.toFixed(2) : unitPrice)}
      base={base ? format(base) : null}
      variant={variant}
      free={free}
    />
  )
}
